import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useUsers, useUserOperations } from "../../../hooks/useUsers";
import { useCrudModals } from "../../../hooks/useCrudModals";
import { UserService } from "../../../services/userService";
import {
  BiaFitUser,
  CreateUserInput,
  UpdateUserInput,
  SubscriptionProvider,
  SubscriptionStatus,
  UserFilters,
} from "../../../types/user";
import UsersTable from "../../../components/users/UsersTable";
import UserModal from "../../../components/users/UserModal";
import DeleteConfirmationModal from "../../../components/modals/DeleteConfirmationModal";
import Pagination from "../../../components/common/Pagination";
import ListPageShell from "../../../components/common/ListPageShell";
import { logger } from "../../../utils/logger";

const Users: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const modals = useCrudModals<BiaFitUser>();
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const initialSearchTerm = searchParams.get("search") || "";
  const initialProvider = searchParams.get("provider") as
    | SubscriptionProvider
    | undefined;
  const initialStatus = searchParams.get("status") as
    | SubscriptionStatus
    | undefined;
  const initialChallengeParticipant = searchParams.get(
    "is_challenge_participant"
  );
  const initialChallengeId = searchParams.get("challenge_id");
  const initialWithTrashed = searchParams.get("with_trashed");

  const initialFilters: UserFilters = {};
  if (initialSearchTerm) initialFilters.search = initialSearchTerm;
  if (initialProvider) initialFilters.subscription_provider = initialProvider;
  if (initialStatus) initialFilters.subscription_status = initialStatus;
  if (initialChallengeParticipant !== null) {
    initialFilters.is_challenge_participant =
      initialChallengeParticipant === "1";
  }
  if (initialChallengeId) {
    initialFilters.challenge_id = parseInt(initialChallengeId, 10);
  }
  // Default with_trashed to true if not specified in URL
  initialFilters.with_trashed =
    initialWithTrashed === null ? true : initialWithTrashed === "true";

  const {
    users,
    loading,
    error,
    total,
    page,
    limit,
    totalPages,
    changePage,
    refresh,
    filters,
    updateFilters,
  } = useUsers(initialPage, 50, initialFilters);

  useEffect(() => {
    const params: Record<string, string> = {};

    if (filters.with_trashed !== undefined) {
      params.with_trashed = filters.with_trashed ? "true" : "false";
    }
    params.page = page.toString();

    if (filters.search) {
      params.search = filters.search;
    }
    if (filters.subscription_provider) {
      params.provider = filters.subscription_provider;
    }
    if (filters.subscription_status) {
      params.status = filters.subscription_status;
    }
    if (filters.is_challenge_participant !== undefined) {
      params.is_challenge_participant = filters.is_challenge_participant
        ? "1"
        : "0";
    }
    if (filters.challenge_id) {
      params.challenge_id = filters.challenge_id.toString();
    }

    setSearchParams(params, { replace: true });
  }, [page, filters, setSearchParams]);

  const {
    loading: operationLoading,
    createUser,
    updateUser,
    deleteUser,
    error: operationError,
  } = useUserOperations();

  const handleViewUser = (user: BiaFitUser) => {
    navigate(`/admin/users/${user.id}`, { state: { from: location.search } });
  };

  const confirmDeleteUser = async () => {
    if (!modals.toDelete) return;

    const success = await deleteUser(modals.toDelete.id);
    if (success) {
      refresh();
      modals.closeDelete();
    }
  };

  const handleSearch = (searchTerm: string) => {
    updateFilters({
      ...filters,
      search: searchTerm,
    });
  };

  const handleClearSearch = () => {
    updateFilters({
      ...filters,
      search: undefined,
    });
  };

  const handleSubscriptionProviderChange = (
    provider: SubscriptionProvider | undefined
  ) => {
    if (provider === "trial" && filters.subscription_status) {
      const validTrialStatuses: SubscriptionStatus[] = ["active", "expired"];
      const shouldResetStatus = !validTrialStatuses.includes(
        filters.subscription_status
      );

      updateFilters({
        ...filters,
        subscription_provider: provider,
        subscription_status: shouldResetStatus
          ? undefined
          : filters.subscription_status,
      });
    } else {
      updateFilters({
        ...filters,
        subscription_provider: provider,
      });
    }
  };

  const handleSubscriptionStatusChange = (
    status: SubscriptionStatus | undefined
  ) => {
    updateFilters({
      ...filters,
      subscription_status: status,
    });
  };

  const handleChallengeParticipantChange = (
    isParticipant: boolean | undefined
  ) => {
    updateFilters({
      ...filters,
      is_challenge_participant: isParticipant,
      challenge_id: isParticipant === true ? filters.challenge_id : undefined,
    });
  };

  const handleChallengeChange = (challengeId: number | undefined) => {
    updateFilters({
      ...filters,
      challenge_id: challengeId,
      is_challenge_participant:
        challengeId !== undefined ? true : filters.is_challenge_participant,
    });
  };

  const handleWithTrashedChange = (withTrashed: boolean) => {
    updateFilters({
      ...filters,
      with_trashed: withTrashed,
    });
  };

  const handleDownloadCsv = async () => {
    setDownloadingCsv(true);
    try {
      await UserService.exportUsersCsv(filters);
    } catch (err) {
      logger.error("Failed to download CSV:", err);
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleCreateSubmit = async (userData: any) => {
    const createData: CreateUserInput = {
      email: userData.email,
      first_name: userData.first_name,
      last_name: userData.last_name,
      password: userData.password,
      gender: userData.gender,
      date_of_birth: userData.date_of_birth,
      height: userData.height,
    };

    const newUser = await createUser(createData);
    if (newUser) {
      refresh();
      modals.closeCreate();
    }
  };

  const handleUpdateSubmit = async (userData: any) => {
    if (!modals.selected) return;

    const updateData: UpdateUserInput = {
      first_name: userData.first_name,
      last_name: userData.last_name,
      gender: userData.gender,
      date_of_birth: userData.date_of_birth,
      height: userData.height,
    };

    const updatedUser = await updateUser(modals.selected.id, updateData);
    if (updatedUser) {
      refresh();
      modals.closeEdit();
    }
  };

  return (
    <>
      <ListPageShell
        entityName="users"
        onRefresh={refresh}
        refreshDisabled={loading}
        onAdd={modals.openCreate}
        addLabel="Add User"
        error={error}
        operationError={operationError}
      >
        <UsersTable
          users={users}
          loading={loading || operationLoading}
          onEdit={modals.openEdit}
          onDelete={modals.openDelete}
          onView={handleViewUser}
          onSearch={handleSearch}
          onClearSearch={handleClearSearch}
          searchTerm={filters.search || ""}
          isSearchMode={!!filters.search}
          subscriptionProvider={filters.subscription_provider}
          subscriptionStatus={filters.subscription_status}
          isChallengeParticipant={filters.is_challenge_participant}
          challengeId={filters.challenge_id}
          withTrashed={filters.with_trashed}
          onSubscriptionProviderChange={handleSubscriptionProviderChange}
          onSubscriptionStatusChange={handleSubscriptionStatusChange}
          onChallengeParticipantChange={handleChallengeParticipantChange}
          onChallengeChange={handleChallengeChange}
          onWithTrashedChange={handleWithTrashedChange}
          onDownloadCsv={handleDownloadCsv}
          downloadingCsv={downloadingCsv}
        />

        <Pagination
          page={page}
          limit={limit}
          total={total}
          totalPages={totalPages}
          entityName="users"
          isSearchMode={!!filters.search}
          onPageChange={changePage}
          loading={loading}
        />
      </ListPageShell>

      <UserModal
        isOpen={modals.showCreate}
        onClose={modals.closeCreate}
        onSubmit={handleCreateSubmit}
        mode="create"
        loading={operationLoading}
        error={operationError}
      />

      <UserModal
        isOpen={modals.showEdit}
        onClose={modals.closeEdit}
        onSubmit={handleUpdateSubmit}
        user={modals.selected}
        mode="edit"
        loading={operationLoading}
        error={operationError}
      />

      <DeleteConfirmationModal
        isOpen={modals.showDelete}
        onClose={modals.closeDelete}
        onConfirm={confirmDeleteUser}
        title="Delete User"
        message="Are you sure you want to delete this user?"
        itemName={
          modals.toDelete
            ? `${modals.toDelete.first_name} ${modals.toDelete.last_name}`.trim() ||
              modals.toDelete.email
            : ""
        }
        loading={operationLoading}
        error={operationError}
      />
    </>
  );
};

export default Users;
